import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import appointmentService from "../services/appointmentService";
import patientService from "../services/patientService";
import AppointmentModal from "./AppointmentModal";
import Toast from "./Toast";
import "../styles/PatientDetailPage.css";

const PatientDetailPage = () => {
  const { patientId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const authState = useSelector((state) => state.auth);
  const isAdmin = useSelector((state) => state.admin.isAuthenticated);
  const doctorId = authState.doctor?.doctorId;

  // Helper function to calculate age from date of birth
  const calculateAge = (dob) => {
    if (!dob) return null;
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Helper function to format date string (YYYY-MM-DD) to locale date without timezone issues
  const formatDateString = (dateString) => {
    if (!dateString) return "—";
    try {
      const [year, month, day] = dateString.split('-');
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString();
    } catch (err) {
      return "—";
    }
  };

  // Helper function to calculate DOB from age
  const calculateDOB = (age) => {
    if (!age) return null;
    const today = new Date();
    const dob = new Date(today.getFullYear() - age, today.getMonth(), today.getDate());
    const year = dob.getFullYear();
    const month = String(dob.getMonth() + 1).padStart(2, '0');
    const day = String(dob.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [patient, setPatient] = useState(location.state?.patient || null);
  const [appointments, setAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(!patient);
  const [appointmentsLoading, setAppointmentsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    landline_number: "",
    age: "",
    date_of_birth: "",
    gender: "",
    nid_number: "",
    address: "",
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchPatientDetails = async () => {
      try {
        setIsLoading(true);
        const data = await patientService.getPatientById(patientId, doctorId, isAdmin);
        
        // Authorization check: verify patient belongs to logged-in doctor or user is admin
        if (!isAdmin && data.doctor_id !== parseInt(doctorId)) {
          setError("Unauthorized: You don't have access to this patient");
          setTimeout(() => {
            navigate("/dashboard", { state: { activeSection: 'patients' } });
          }, 2000);
          return;
        }
        
        setPatient(data);
        setError(null);
      } catch (err) {
        setError("Failed to load patient details");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchPatientAppointments = async () => {
      try {
        setAppointmentsLoading(true);
        const data = await appointmentService.getAppointmentsByPatient(patientId);
        setAppointments(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load appointments:", err);
        setAppointments([]);
      } finally {
        setAppointmentsLoading(false);
      }
    };

    if (!patient) {
      fetchPatientDetails();
    }
    fetchPatientAppointments();
  }, [patientId, patient, doctorId, isAdmin, navigate]);

  // Initialize formData when patient changes
  useEffect(() => {
    if (patient) {
      // Extract just the date part (YYYY-MM-DD) to avoid timezone issues
      let dateOfBirth = "";
      if (patient.date_of_birth) {
        dateOfBirth = patient.date_of_birth.split('T')[0] || patient.date_of_birth;
      }
      
      setFormData({
        first_name: patient.first_name || "",
        last_name: patient.last_name || "",
        email: patient.email || "",
        phone_number: patient.phone_number || "",
        landline_number: patient.landline_number || "",
        age: patient.age || "",
        date_of_birth: dateOfBirth,
        gender: patient.gender || "",
        nid_number: patient.nid_number || "",
        address: patient.address || "",
      });
    }
  }, [patient, isEditMode]);

  const handleAppointmentClick = (appointment) => {
    // Open consultation in the same component or new tab if needed
    window.open(`/consultation/${appointment.appointment_id}`, '_blank');
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleEditClick = () => {
    setIsEditMode(true);
    setError(null);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    // Reset form data to original patient data
    if (patient) {
      // Extract just the date part (YYYY-MM-DD) to avoid timezone issues
      let dateOfBirth = "";
      if (patient.date_of_birth) {
        dateOfBirth = patient.date_of_birth.split('T')[0] || patient.date_of_birth;
      }
      
      setFormData({
        first_name: patient.first_name || "",
        last_name: patient.last_name || "",
        email: patient.email || "",
        phone_number: patient.phone_number || "",
        landline_number: patient.landline_number || "",
        age: patient.age || "",
        date_of_birth: dateOfBirth,
        gender: patient.gender || "",
        nid_number: patient.nid_number || "",
        address: patient.address || "",
      });
    }
    setError(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'date_of_birth') {
      // Auto-calculate age when DOB changes
      const calculatedAge = calculateAge(value);
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        age: calculatedAge,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      // Validate required fields
      if (!formData.first_name.trim() || !formData.last_name.trim() || !formData.phone_number.trim()) {
        setError("First Name, Last Name, and Phone Number are required");
        return;
      }

      // Extract just the date part (YYYY-MM-DD) to avoid timezone issues
      let dateOfBirth = null;
      if (formData.date_of_birth) {
        // If it's a date string with time (ISO format), extract just the date part
        dateOfBirth = formData.date_of_birth.split('T')[0] || formData.date_of_birth;
      }

      // Prepare update data
      const updateData = {
        firstName: formData.first_name,
        lastName: formData.last_name,
        email: formData.email || null,
        phoneNumber: formData.phone_number,
        landlineNumber: formData.landline_number || null,
        age: formData.age ? parseInt(formData.age) : null,
        dateOfBirth: dateOfBirth,
        gender: formData.gender || null,
        nidNumber: formData.nid_number || null,
        address: formData.address || null,
      };

      const updatedPatient = await patientService.updatePatient(patientId, updateData);
      setPatient(updatedPatient);
      setIsEditMode(false);
      setSuccessMessage("Patient information updated successfully!");

      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      setError(err.message || "Failed to update patient information");
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="patient-detail-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading patient details...</p>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="patient-detail-page">
        <div className="error-container">
          <p className="error-message">Patient not found</p>
          <button className="back-button" onClick={handleBack}>
            ← Return
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="patient-detail-page">
      <Toast
        message={successMessage}
        type="success"
        onClose={() => setSuccessMessage(null)}
      />
      <AppointmentModal
        isOpen={isAppointmentModalOpen}
        onClose={() => {
          setIsAppointmentModalOpen(false);
          // Refresh appointments list when modal closes
          const fetchAppointments = async () => {
            try {
              setAppointmentsLoading(true);
              const data = await appointmentService.getAppointmentsByPatient(patientId);
              setAppointments(data || []);
            } catch (err) {
              console.error("Failed to load appointments:", err);
            } finally {
              setAppointmentsLoading(false);
            }
          };
          fetchAppointments();
        }}
        preSelectedPatientId={patientId}
      />

      <div className="patient-header">
        <button className="back-button" onClick={handleBack}>
          ← Return
        </button>
        <h1>Patient Details</h1>
      </div>

      <div className="patient-container">
        {error && <div className="error-banner">{error}</div>}

        {/* Patient Information Section */}
        <div className="patient-info-section">
          <div className="section-header">
            <h2>👤 Personal Information</h2>
            {!isEditMode && (
              <button className="edit-button" onClick={handleEditClick}>
                ✎ Edit
              </button>
            )}
          </div>

          <div className="info-grid">
            <div className="info-card">
              <div className="info-field">
                <label>First Name <span style={{color: 'red'}}>*</span></label>
                {isEditMode ? (
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    className="info-input"
                    placeholder="Enter first name"
                  />
                ) : (
                  <p>{patient.first_name}</p>
                )}
              </div>
              <div className="info-field">
                <label>Last Name <span style={{color: 'red'}}>*</span></label>
                {isEditMode ? (
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className="info-input"
                    placeholder="Enter last name"
                  />
                ) : (
                  <p>{patient.last_name}</p>
                )}
              </div>
            </div>

            <div className="info-card">
              <div className="info-field">
                <label>Email</label>
                {isEditMode ? (
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="info-input"
                    placeholder="Enter email"
                  />
                ) : (
                  <p>{patient.email || "—"}</p>
                )}
              </div>
              <div className="info-field">
                <label>Phone <span style={{color: 'red'}}>*</span></label>
                {isEditMode ? (
                  <input
                    type="tel"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    className="info-input"
                    placeholder="Enter phone number"
                  />
                ) : (
                  <p>{patient.phone_number || "—"}</p>
                )}
              </div>
              <div className="info-field">
                <label>Landline</label>
                {isEditMode ? (
                  <input
                    type="tel"
                    name="landline_number"
                    value={formData.landline_number}
                    onChange={handleInputChange}
                    className="info-input"
                    placeholder="Enter landline"
                  />
                ) : (
                  <p>{patient.landline_number || "—"}</p>
                )}
              </div>
            </div>

            <div className="info-card">
              <div className="info-field">
                <label>Date of Birth</label>
                {isEditMode ? (
                  <input
                    type="date"
                    name="date_of_birth"
                    value={formData.date_of_birth ? formData.date_of_birth.split('T')[0] : (calculateDOB(formData.age) || "")}
                    onChange={handleInputChange}
                    className="info-input"
                    placeholder="Enter date of birth"
                  />
                ) : (
                  <p>{formatDateString(formData.date_of_birth)}</p>
                )}
              </div>
              <div className="info-field">
                <label>Age</label>
                <p>{formData.age || "—"}</p>
              </div>
            </div>

            <div className="info-card">
              <div className="info-field">
                <label>Gender</label>
                {isEditMode ? (
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="info-input"
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                ) : (
                  <p>{patient.gender || "—"}</p>
                )}
              </div>
              <div className="info-field">
                <label>Member Since</label>
                <p>
                  {patient.created_at
                    ? formatDateString(patient.created_at.split('T')[0])
                    : "—"}
                </p>
              </div>
            </div>

            <div className="info-card">
              <div className="info-field">
                <label>NID Number</label>
                {isEditMode ? (
                  <input
                    type="text"
                    name="nid_number"
                    value={formData.nid_number}
                    onChange={handleInputChange}
                    className="info-input"
                    placeholder="Enter NID number"
                  />
                ) : (
                  <p>{patient.nid_number || "—"}</p>
                )}
              </div>
            </div>

            <div className="info-card">
              <div className="info-field">
                <label>Address</label>
                {isEditMode ? (
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="info-input"
                    placeholder="Enter address"
                    rows="3"
                    style={{ resize: 'vertical' }}
                  />
                ) : (
                  <p>{patient.address || "—"}</p>
                )}
              </div>
            </div>
          </div>

          {isEditMode && (
            <div className="edit-actions">
              <button
                className="cancel-button"
                onClick={handleCancel}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                className="save-button"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          )}
        </div>

        {/* Past Appointments Section */}
        <div className="appointments-section">
          <div className="section-header">
            <div className="section-title-group">
              <h2>📅 Past Appointments</h2>
              <span className="appointment-count">
                ({appointments.length})
              </span>
            </div>
            <button
              className="schedule-appointment-btn"
              onClick={() => {
                setIsAppointmentModalOpen(true);
              }}
              title="Schedule a new appointment for this patient"
            >
              Schedule Appointment
            </button>
          </div>

          {appointmentsLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading appointments...</p>
            </div>
          ) : appointments.length === 0 ? (
            <div className="no-appointments">
              <p>No appointments found for this patient</p>
            </div>
          ) : (
            <div className="appointments-table-container">
              <table className="appointments-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Duration</th>
                    <th>Status</th>
                    <th>Fees</th>
                    <th>Remarks</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {appointments.map((appointment) => (
                    <tr
                      key={appointment.appointment_id}
                      className="appointment-row"
                      onClick={() => handleAppointmentClick(appointment)}
                    >
                      <td>
                        {new Date(appointment.appointment_date).toLocaleDateString(
                          "en-US",
                          {
                            weekday: "short",
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          }
                        )}
                      </td>
                      <td>{appointment.appointment_time}</td>
                      <td>{appointment.appointment_duration} min</td>
                      <td>
                        <span
                          className={`status-badge ${appointment.status}`}
                        >
                          {appointment.status}
                        </span>
                      </td>
                      <td>
                        {appointment.fees ? `${parseFloat(appointment.fees).toFixed(2)}` : "—"}
                      </td>
                      <td className="remarks-preview">
                        {appointment.remarks
                          ? appointment.remarks.substring(0, 50) +
                            (appointment.remarks.length > 50 ? "..." : "")
                          : "—"}
                      </td>
                      <td className="action-cell">
                        <span className="view-link">View →</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientDetailPage;
